import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  Search,
  Eye,
  EyeOff,
  Palette,
  RefreshCw,
  Check,
  X,
  Play,
  Pause,
  Focus,
  Volume2,
  VolumeX,
  Database,
} from "lucide-react";

const formatAnimeTitle = (name) => {
  if (!name) return "";
  return name
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const isMobile = window.innerWidth <= 768;

const AnimeGuess = () => {
  const location = useLocation();
  const { userData = [], username = "Player" } = location.state || {};

  const videoRef = useRef(null);
  const retryCountRef = useRef(0);
  const isLoadingAnimeRef = useRef(false);

  const [currentAnime, setCurrentAnime] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [filteredAnime, setFilteredAnime] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(false);

  const [videoHidden, setVideoHidden] = useState(true);
  const [colorHidden, setColorHidden] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [quality, setQuality] = useState(25);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [fullCurrentAnimeData, setFullCurrentAnimeData] = useState({});
  const [isRomanji, setIsRomanji] = useState(false);

  const videoOptions = [
    {
      type: "Openings",
      value: ["OP"],
    },
    {
      type: "Endings",
      value: ["ED"],
    },
    {
      type: "Both",
      value: ["OP,ED"],
    },
  ];

  const [selectedMusic, setSelectedMusic] = useState(videoOptions[0]);

  const api = "https://anime-quiz-backend.vercel.app/api";
  const maxGuesses = 5;
  const maxRetries = 3;

  const blurEffects = {
    25: "blur-lg",
    50: "blur",
    75: "blur-sm",
    100: "",
  };
  const blurEffectClass = blurEffects[quality] || "";

  const videoDynamicClasses = [
    videoHidden ? "opacity-0" : "opacity-100",
    colorHidden ? "grayscale" : "",
    blurEffectClass,
  ]
    .filter(Boolean)
    .join(" ");

  const handleQualityKeyDown = (e) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      setQuality((prev) => Math.max(25, prev - 25 === 0 ? 25 : prev - 25));
    } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      setQuality((prev) => Math.min(100, prev + 25 > 100 ? 100 : prev + 25));
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);

      return () => {
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
      };
    }
  }, [videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    if (userData && userData.length > 0) {
      retryCountRef.current = 0;
      setCurrentAnime(null);
      setVideoUrl("");
      selectRandomAnime();
    }
  }, [userData]);

  useEffect(() => {
    if (currentGuess && userData) {
      const guessedTitles = new Set(guesses.map((g) => g.title));
      const normalized = currentGuess.toLowerCase().trim();
      if (isRomanji) {
        const filtered = userData
          .filter((item) => !guessedTitles.has(item.title))
          .filter((item) =>
            item.title.toLowerCase().replace(/_/g, " ").includes(normalized),
          )
          .slice(0, 10);
        setFilteredAnime(filtered);
        setShowDropdown(filtered.length > 0);
      } else {
        const filtered = userData
          .filter((item) => !guessedTitles.has(item.title))
          .filter((item) =>
            item.enTitle.toLowerCase().replace(/_/g, " ").includes(normalized),
          )
          .slice(0, 10);
        setFilteredAnime(filtered);
        setShowDropdown(filtered.length > 0);
      }
    } else {
      setFilteredAnime([]);
      setShowDropdown(false);
    }
  }, [currentGuess, userData, guesses, isRomanji]);

  const extractVideoLinkFromData = (apiResponseData, animeNameForLogging) => {
    let videoLink = null;

    if (apiResponseData?.anime?.animethemes?.length > 0) {
      const animethemes = apiResponseData.anime.animethemes;
      const randomTheme =
        animethemes[Math.floor(Math.random() * animethemes.length)];

      if (!randomTheme.sequence) {
        setFullCurrentAnimeData(apiResponseData?.anime?.animethemes[0]);
      } else {
        setFullCurrentAnimeData(
          apiResponseData?.anime?.animethemes[randomTheme.sequence - 1],
        );
      }
      if (randomTheme.animethemeentries?.length > 0) {
        const randomEntry =
          randomTheme.animethemeentries[
            Math.floor(Math.random() * randomTheme.animethemeentries.length)
          ];

        if (randomEntry.videos?.length > 0) {
          const randomVideo =
            randomEntry.videos[
              Math.floor(Math.random() * randomEntry.videos.length)
            ];

          videoLink = randomVideo.link;
        }
      }
    } else if (
      apiResponseData?.data?.[0]?.node?.theme?.theme_entry?.video?.link
    ) {
      videoLink = apiResponseData.data[0].node.theme.theme_entry.video.link;
      console.log(
        `Found video link for ${animeNameForLogging} (old nested structure):`,
        videoLink,
      );
    } else if (apiResponseData?.link) {
      videoLink = apiResponseData.link;
      console.log(
        `Found video link for ${animeNameForLogging} (direct format):`,
        videoLink,
      );
    }

    return videoLink;
  };

  const selectRandomAnime = async () => {
    if (isLoadingAnimeRef.current) {
      console.log(
        "selectRandomAnime: An anime selection sequence is already in progress. Exiting.",
      );
      return;
    }
    isLoadingAnimeRef.current = true;
    setLoading(true);

    const fetchAnimeRecursive = async () => {
      if (!userData || userData.length === 0) {
        console.log("User data is empty, cannot select anime.");
        setLoading(false);
        isLoadingAnimeRef.current = false;
        return;
      }

      if (retryCountRef.current >= maxRetries) {
        console.error(
          "Max retries reached. Unable to fetch valid video info after",
          maxRetries,
          "attempts.",
        );
        setLoading(false);
        isLoadingAnimeRef.current = false;
        return;
      }

      const randomItem = userData[Math.floor(Math.random() * userData.length)];
      const { title } = randomItem;
      console.log(
        `Attempting to fetch info for anime: ${title} (Attempt ${
          retryCountRef.current + 1
        }/${maxRetries})`,
      );

      try {
        const response = await fetch(
          `${api}/anime-info?anime_name=${title}&musicType=${selectedMusic.value}`,
        );

        if (!response.ok) {
          console.log("Not able to pull data");
        }

        if (response.ok) {
          const data = await response.json();
          // console.log(`API Response data for ${title}:`, data);

          const videoLink = extractVideoLinkFromData(data, title);

          if (videoLink) {
            // console.log(
            //   "Successfully found video. Setting video URL and current anime:",
            //   title,
            //   "->",
            //   videoLink,
            // );
            setCurrentAnime(title);
            setVideoUrl(videoLink);
            retryCountRef.current = 0;
            setLoading(false);
            isLoadingAnimeRef.current = false;
          } else {
            console.log(
              `No video link found in response for ${title}. Retrying with a new anime...`,
            );
            console.log(
              "Response structure (if no link found by helper):",
              JSON.stringify(data, null, 2),
            );
            retryCountRef.current++;
            await fetchAnimeRecursive();
          }
        } else {
          console.log(
            `API request failed for ${title} with status: ${response.status}. Retrying with a new anime...`,
          );
          retryCountRef.current++;
          await fetchAnimeRecursive();
        }
      } catch (error) {
        console.error(
          `Error fetching video info for ${title}:`,
          error,
          ". Retrying with a new anime...",
        );
        retryCountRef.current++;
        await fetchAnimeRecursive();
      }
    };

    await fetchAnimeRecursive();
  };

  const submitGuess = (guessedItem) => {
    if (gameOver || guesses.length >= maxGuesses) return;

    const isCorrect =
      guessedItem.title.toLowerCase() === currentAnime?.toLowerCase();
    const newGuess = {
      title: guessedItem.title,
      correct: isCorrect,
    };

    const newGuesses = [...guesses, newGuess];
    setGuesses(newGuesses);

    setCurrentGuess("");
    setShowDropdown(false);

    if (isCorrect) {
      setScore(score + 1);
      setGameOver(true);
    } else if (newGuesses.length >= maxGuesses) {
      setGameOver(true);
    }
  };

  const handleGiveUp = () => {
    if (gameOver) return;
    setGaveUp(true);
    setGameOver(true);
  };

  const resetGame = () => {
    setGuesses([]);
    setGameOver(false);
    setGaveUp(false);
    setCurrentGuess("");
    setVideoHidden(true);
    setColorHidden(true);
    setFullCurrentAnimeData({});
    setQuality(25);
    retryCountRef.current = 0;
    setCurrentAnime(null);
    setVideoUrl("");
    selectRandomAnime();
  };

  const cycleQuality = () => {
    setQuality((prev) => {
      if (prev === 25) return 50;
      if (prev === 50) return 75;
      if (prev === 75) return 100;
      return 25;
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && filteredAnime.length > 0) {
      submitGuess(filteredAnime[0]);
    }
  };

  if (!userData || userData.length === 0) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--md-sys-color-background)" }}
      >
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[var(--md-sys-color-on-background)] mb-4">
            No anime data found
          </h2>
          <p className="text-[var(--md-sys-color-on-surface-variant)]">
            Please go back and enter a valid username.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-[calc(100vh-8vh)] overflow-hidden flex flex-col subtle-pattern"
      style={{ backgroundColor: "var(--md-sys-color-background)" }}
    >
      <div className="container mx-auto px-4 py-3 md:py-5 flex-1 flex flex-col">
        <div className="text-center mb-3 md:mb-3 animate-fade-in-up">
          <h1 className="text-3xl md:text-4xl font-bold text-[var(--md-sys-color-on-background)] mb-3 tracking-tight">
            Anime
            {selectedMusic.type == "Openings"
              ? " Opening "
              : selectedMusic.type == "Endings"
                ? " Ending "
                : " Song "}
            Guess
          </h1>
          <p className="text-[var(--md-sys-color-on-surface-variant)]">
            Playing with {username}'s anime list
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-2 md:gap-4">
            <span
              className="px-3 md:px-4 py-1.5 rounded-full text-[var(--md-sys-color-on-primary-container)] font-semibold text-sm md:text-lg"
              style={{
                backgroundColor: "var(--md-sys-color-primary-container)",
              }}
            >
              Score: {score}
            </span>
            <button
              onClick={resetGame}
              className="flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-xl text-xs md:text-sm text-[var(--md-sys-color-on-primary-container)] transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
              style={{
                backgroundColor: "var(--md-sys-color-primary-container)",
              }}
            >
              <RefreshCw className="w-3.5 h-3.5 md:w-4 md:h-4" />
              New{" "}
              {selectedMusic.type == "Openings"
                ? " Opening "
                : selectedMusic.type == "Endings"
                  ? " Ending "
                  : " Song "}
            </button>
            <button
              onClick={handleGiveUp}
              disabled={
                gameOver || Object.keys(fullCurrentAnimeData).length === 0
              }
              className="flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-xl text-xs md:text-sm text-[var(--md-sys-color-on-error-container)] transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{
                backgroundColor: "var(--md-sys-color-error-container)",
              }}
            >
              <X className="w-3.5 h-3.5 md:w-4 md:h-4" />
              Give Up
            </button>
            <div className="flex gap-2">
              <button
                className={`px-2.5 md:px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 ${
                  selectedMusic.type == "Openings"
                    ? "text-[var(--md-sys-color-on-primary-container)] shadow-md"
                    : "text-[var(--md-sys-color-on-surface-variant)]"
                }`}
                style={{
                  backgroundColor:
                    selectedMusic.type == "Openings"
                      ? "var(--md-sys-color-primary-container)"
                      : "var(--md-sys-color-surface-container)",
                }}
                onClick={() => setSelectedMusic(videoOptions[0])}
              >
                OP
              </button>
              <button
                className={`px-2.5 md:px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 ${
                  selectedMusic.type == "Endings"
                    ? "text-[var(--md-sys-color-on-primary-container)] shadow-md"
                    : "text-[var(--md-sys-color-on-surface-variant)]"
                }`}
                style={{
                  backgroundColor:
                    selectedMusic.type == "Endings"
                      ? "var(--md-sys-color-primary-container)"
                      : "var(--md-sys-color-surface-container)",
                }}
                onClick={() => setSelectedMusic(videoOptions[1])}
              >
                ED
              </button>
              <button
                className={`px-2.5 md:px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 ${
                  selectedMusic.type == "Both"
                    ? "text-[var(--md-sys-color-on-primary-container)] shadow-md"
                    : "text-[var(--md-sys-color-on-surface-variant)]"
                }`}
                style={{
                  backgroundColor:
                    selectedMusic.type == "Both"
                      ? "var(--md-sys-color-primary-container)"
                      : "var(--md-sys-color-surface-container)",
                }}
                onClick={() => setSelectedMusic(videoOptions[2])}
              >
                Both
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center">
          <div className="mb-6 md:mb-8">
            <div className="relative">
              {loading ? (
                <div
                  className="w-[min(560px,90vw)] md:w-[640px] h-[min(315px,50.6vw)] md:h-[360px] flex items-center justify-center rounded-2xl shadow-xl"
                  style={{
                    backgroundColor: "var(--md-sys-color-surface-container)",
                  }}
                  aria-busy="true"
                  aria-label="Loading video"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--md-sys-color-on-surface)]"></div>
                    <p className="text-[var(--md-sys-color-on-surface-variant)]">
                      Loading anime...
                    </p>
                  </div>
                </div>
              ) : videoUrl ? (
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-[var(--md-sys-color-primary)] to-[var(--md-sys-color-secondary)] rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                  <video
                    ref={videoRef}
                    controls
                    autoPlay
                    loop
                    playsInline
                    disablePictureInPicture
                    controlsList={isMobile ? "nofullscreen" : ""}
                    className={`relative w-[min(560px,90vw)] md:w-[640px] h-[min(315px,50.6vw)] md:h-[360px] rounded-xl object-cover shadow-xl ${videoDynamicClasses}`}
                    style={{
                      imageRendering: quality <= 50 ? "pixelated" : "auto",
                    }}
                  >
                    <source src={videoUrl} type="video/webm" />
                    Your browser does not support the video tag.
                  </video>
                  {videoHidden ? (
                    <div className="absolute inset-0 bg-[var(--md-sys-color-scrim)] rounded-xl flex items-center justify-center pointer-events-none">
                      {isPlaying ? (
                        <Pause className="text-[var(--md-sys-color-on-surface)] w-16 h-16 animate-pulse-soft" />
                      ) : (
                        <Play className="text-[var(--md-sys-color-on-surface)] w-16 h-16" />
                      )}
                    </div>
                  ) : (
                    <div>
                      {isMobile && (
                        <button
                          className="absolute bottom-4 right-4 p-2.5 rounded-full shadow-lg hover:scale-110 transition-transform bg-[var(--md-sys-color-surface-container)]/90"
                          onClick={(e) => {
                            e.preventDefault();
                            if (videoRef.current) {
                              if (videoRef.current.paused) {
                                videoRef.current.play();
                              } else {
                                videoRef.current.pause();
                              }
                            }
                          }}
                          aria-label={isPlaying ? "Pause video" : "Play video"}
                        >
                          {isPlaying ? (
                            <Pause className="text-[var(--md-sys-color-on-surface)] w-5 h-5" />
                          ) : (
                            <Play className="text-[var(--md-sys-color-on-surface)] w-5 h-5 ml-0.5" />
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className="w-[min(560px,90vw)] md:w-[640px] h-[min(315px,50.6vw)] md:h-[360px] flex items-center justify-center rounded-2xl shadow-xl"
                  style={{
                    backgroundColor: "var(--md-sys-color-surface-container)",
                  }}
                >
                  <p className="text-[var(--md-sys-color-on-surface)]">
                    No video available
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-4 md:mb-4">
            <button
              onClick={() => setVideoHidden(!videoHidden)}
              aria-pressed={videoHidden}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[var(--md-sys-color-on-primary-container)] transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
              style={{
                backgroundColor: "var(--md-sys-color-primary-container)",
              }}
            >
              {videoHidden ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
              {videoHidden ? "Show Video" : "Hide Video"}
            </button>

            <button
              onClick={() => setColorHidden(!colorHidden)}
              aria-pressed={colorHidden}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[var(--md-sys-color-on-secondary-container)] transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
              style={{
                backgroundColor: "var(--md-sys-color-secondary-container)",
              }}
            >
              <Palette className="w-4 h-4" />
              {colorHidden ? "Show Color" : "Hide Color"}
            </button>

            <div
              role="slider"
              aria-valuemin="25"
              aria-valuemax="100"
              aria-valuenow={quality}
              aria-orientation="horizontal"
              aria-label={`Video quality ${quality}%`}
              tabIndex="0"
              onClick={cycleQuality}
              onKeyDown={handleQualityKeyDown}
              className="flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-xl text-[var(--md-sys-color-on-tertiary-container)] transition-all duration-300 hover:scale-105 hover:shadow-lg min-w-[110px] md:min-w-[120px] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--md-sys-color-outline)]"
              style={{
                backgroundColor: "var(--md-sys-color-tertiary-container)",
              }}
            >
              <Focus className="w-4 h-4" />
              <div className="flex flex-col items-center">
                <span className="text-xs font-medium">Quality</span>
                <div className="flex items-center gap-1">
                  <div className="w-8 h-1.5 bg-[var(--md-sys-color-surface-variant)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--md-sys-color-on-tertiary-container)] rounded-full transition-all duration-300"
                      style={{ width: `${quality}%` }}
                    ></div>
                  </div>
                  <span className="text-xs font-semibold">{quality}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setIsMuted(!isMuted)}
              className="flex items-center justify-center px-3 py-2.5 rounded-xl text-[var(--md-sys-color-on-secondary-container)] transition-all duration-300 hover:scale-105 hover:shadow-lg min-w-[40px]"
              style={{
                backgroundColor: "var(--md-sys-color-secondary-container)",
              }}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>

            <div
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-[var(--md-sys-color-on-secondary-container)] transition-all duration-300 hover:scale-105 hover:shadow-lg min-w-[100px] md:min-w-[120px] focus:outline-none focus:ring-2 focus:ring-[var(--md-sys-color-outline)]"
              style={{
                backgroundColor: "var(--md-sys-color-secondary-container)",
              }}
            >
              <div className="flex flex-col items-center w-full">
                <div className="flex items-center gap-1 w-full">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={isMuted ? 0 : volume}
                    onChange={(e) => {
                      setVolume(parseInt(e.target.value));
                      if (parseInt(e.target.value) === 0) {
                        setIsMuted(true);
                      } else if (isMuted) {
                        setIsMuted(false);
                      }
                    }}
                    className="w-12 md:w-16 h-1.5 appearance-none bg-[var(--md-sys-color-surface-variant)] rounded-full cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--md-sys-color-on-secondary-container)] [&::-webkit-slider-thumb]:cursor-pointer"
                    aria-label={`Volume ${volume}%`}
                  />
                  <span className="text-xs font-semibold min-w-[24px] text-right">
                    {isMuted ? 0 : volume}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center mb-4 md:mb-4">
            <div className="flex gap-2 md:gap-3">
              {Array.from({ length: maxGuesses }, (_, index) => (
                <div
                  key={index}
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border-2 relative group transition-all duration-300 hover:scale-110"
                  style={{
                    backgroundColor:
                      index < guesses.length
                        ? guesses[index]?.correct
                          ? "var(--md-sys-color-primary)"
                          : "var(--md-sys-color-error)"
                        : "var(--md-sys-color-surface-variant)",
                    borderColor: "var(--md-sys-color-outline)",
                    boxShadow:
                      index < guesses.length
                        ? "0 4px 12px rgba(0,0,0,0.2)"
                        : "none",
                  }}
                >
                  {index < guesses.length &&
                    (guesses[index].correct ? (
                      <Check className="w-5 h-5 md:w-6 md:h-6 text-[var(--md-sys-color-on-primary)]" />
                    ) : (
                      <>
                        <X className="w-5 h-5 md:w-6 md:h-6 text-[var(--md-sys-color-on-error)]" />
                        <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-[var(--md-sys-color-inverse-surface)] text-[var(--md-sys-color-inverse-on-surface)] px-3 py-1.5 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20 shadow-lg">
                          {formatAnimeTitle(guesses[index].title)}
                        </div>
                      </>
                    ))}
                </div>
              ))}
            </div>
          </div>
          <NavLink to="/">
            <div className="text-gray-500 text-sm hover:underline">
              Want to switch the anime deck? Click Here!
            </div>
          </NavLink>
        </div>

        <div className="mt-4 pb-2 md:pb-4">
          {!gameOver && (
            <div className="max-w-md mx-auto mb-4 relative animate-fade-in-up">
              <div className="relative w-full">
                <Search className="absolute left-3.5 top-1/2  -translate-y-1/2 text-[var(--md-sys-color-on-surface-variant)] w-5 h-5" />
                <div
                  className=" absolute right-5 top-1/2 -translate-y-1/2 text-[var(--md-sys-color-on-surface-variant)] text-xs hover:cursor-pointer"
                  onClick={() => setIsRomanji(!isRomanji)}
                >
                  <span
                    className={`text-xs  ${!isRomanji ? "text-[var(--md-sys-color-background)]" : "text-[var(--md-sys-color-on-surface-variant)]"}`}
                  >
                    En{" "}
                  </span>
                  |
                  <span
                    className={`text-xs  ${isRomanji ? "text-[var(--md-sys-color-background)]" : "text-[var(--md-sys-color-on-surface-variant)]"}`}
                  >
                    {" "}
                    Jp
                  </span>
                </div>
                <input
                  type="text"
                  value={currentGuess}
                  onChange={(e) => setCurrentGuess(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Type anime name..."
                  className="w-full pl-11 pr-24 py-3.5  rounded-xl border-2 border-transparent focus:border-[var(--md-sys-color-primary)] focus:outline-none text-[var(--md-sys-color-on-surface)] placeholder-[var(--md-sys-color-on-surface-variant)] shadow-inner transition-all duration-300"
                  style={{
                    backgroundColor: "var(--md-sys-color-surface-bright)",
                  }}
                  aria-label="Enter your anime guess"
                />
              </div>
              {showDropdown && (
                <div
                  className="absolute bottom-full left-0 right-0 mb-2 rounded-xl shadow-xl max-h-60 overflow-y-auto z-10 border"
                  style={{
                    backgroundColor:
                      "var(--md-sys-color-surface-container-low)",
                    borderColor: "var(--md-sys-color-outline-variant)",
                  }}
                  role="listbox"
                  aria-label="Anime suggestions"
                >
                  {filteredAnime.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => submitGuess(item)}
                      className="px-4 py-3 hover:opacity-80 cursor-pointer text-[var(--md-sys-color-on-surface)] border-b border-[var(--md-sys-color-outline-variant)] last:border-b-0 transition-colors duration-150"
                      style={{
                        backgroundColor:
                          index % 2 === 0
                            ? "var(--md-sys-color-surface-container-low)"
                            : "var(--md-sys-color-surface-container-lowest)",
                      }}
                      role="option"
                      aria-selected={false}
                    >
                      <div>
                        {isRomanji
                          ? formatAnimeTitle(item.title)
                          : formatAnimeTitle(item.enTitle)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {gameOver && (
            <div
              className="max-w-md mx-auto text-center p-6 rounded-2xl mb-4 shadow-xl animate-fade-in-up"
              style={{
                backgroundColor: "var(--md-sys-color-surface-container-high)",
              }}
              aria-live="polite"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--md-sys-color-on-surface)] mb-4">
                {gaveUp
                  ? "Gave Up"
                  : guesses[guesses.length - 1]?.correct
                    ? "Correct!"
                    : "Game Over"}
              </h2>
              <p className="text-[var(--md-sys-color-on-surface-variant)] ">
                Anime:{" "}
                <span className="text-[var(--md-sys-color-on-surface)] font-bold">
                  {formatAnimeTitle(currentAnime) || "Unknown"}
                </span>
              </p>
              <p className="text-[var(--md-sys-color-on-surface-variant)] ">
                Song:{" "}
                <span className="text-[var(--md-sys-color-on-surface)] font-bold">
                  {fullCurrentAnimeData?.song?.title || "Unknown"}
                </span>
              </p>
              <p className="text-[var(--md-sys-color-on-surface-variant)] ">
                Artist:{" "}
                <span className="text-[var(--md-sys-color-on-surface)] font-bold">
                  {fullCurrentAnimeData?.song?.artists?.[0]?.name || "Unknown"}
                </span>
              </p>
              <p className="text-[var(--md-sys-color-on-surface-variant)] mb-6 md:mb-8">
                Guesses:{" "}
                <span className="text-[var(--md-sys-color-on-surface)] font-bold">
                  {guesses.length}/{maxGuesses}
                </span>
              </p>
              <button
                onClick={resetGame}
                className="px-6 py-3 rounded-xl text-[var(--md-sys-color-on-primary-container)] font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
                style={{
                  backgroundColor: "var(--md-sys-color-primary-container)",
                }}
              >
                Play Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnimeGuess;
